/**
 * THE TWO FORMS OF AN ELEMENT IDENTIFIER, TRANSLATED AT THE BORDER — CCT-354.
 *
 * ⚠️ THIS COST FOUR ROUND TRIPS IN ONE WEEK, AND EVERY TIME THE SYMPTOM WAS A SCREEN THAT SHOWED
 * NOTHING, WITHOUT AN ERROR:
 *
 *   * CCT-349 — `/me/stories/chats` served `107599` where the played scenario carries
 *     `el-107599`: clicking a conversation opened nothing at all;
 *   * CCT-350 — the character's timeline carries `el-107600`, the task list `107600`: the
 *     brand-new filter matched no message and emptied the screen;
 *   * CCT-352 — same pair again, in the task bubble's chips;
 *   * and, server side, `context_for_engine` handed a string to an INTEGER column, so every
 *     story variable came back unresolved.
 *
 * The database numbers elements (`107599`); the app names them (`el-107599`, the contract served
 * by `story_chapter_service`). Both forms are legitimate — what is not is comparing them raw, or
 * translating them a fifth time in a fifth file.
 *
 * ⚠️ SO THE TRANSLATION LIVES HERE, AND NOWHERE ELSE. `e2e/element-id-border.test.mjs` fails when
 * another file strips or adds the `el-` prefix by hand. A rule that depends on remembering it is
 * not a rule; this one is checked.
 */

/** The form the APP uses everywhere: navigation params, scenario item ids, deep-links. */
export function appElementId(raw: string | number | null | undefined): string {
  const bare = engineElementId(raw);
  return bare ? `el-${bare}` : '';
}

/** The form the DATABASE uses: `mission_userstory.element_id`, `bo_story_element.elementid`. */
export function engineElementId(raw: string | number | null | undefined): string {
  return String(raw ?? '').trim().replace(/^el-/i, '');
}

/**
 * Do these two designate the SAME element, whichever form each of them is in?
 *
 * Use this instead of `===`: comparing `'el-42'` with `'42'` is false, and the code that does it
 * looks perfectly correct — it just never matches.
 */
export function sameElement(a: string | number | null | undefined,
                            b: string | number | null | undefined): boolean {
  const x = engineElementId(a);
  const y = engineElementId(b);
  return !!x && x === y;
}

/**
 * Alias of `engineElementId`, kept because a neighbouring session was already importing this name
 * when this module landed.
 *
 * ⚠️ ONE MEANING, TWO NAMES, IS HOW A RULE STARTS DRIFTING. This one exists to avoid breaking an
 * edit in flight, not as a second way of saying it: new code calls `engineElementId` (what the
 * form IS) or `sameElement` (what one usually wants), and this alias goes the day nothing imports
 * it any more.
 */
export const elementKey = engineElementId;
